import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import { getSubdomainTenant } from "../tenant";
import type {
  CatalogCollection,
  DetectedObject,
  NormalizedQuad,
  PostcardLandingPayload,
  PostcardPreset,
  TopProfile,
} from "../api/types";
import type { DoorSize } from "../geometry/doorSize";

// ---------------------------------------------------------------------------
// Visual-designer architecture:
//   document  — normalized, serializable, reconstructable (persisted to localStorage via
//               durableSessionStorage: 7-day TTL, dealer-scope-stamped). The only
//               slice we'd ever track for undo/redo (zundo `temporal`) later.
//   ui        — ephemeral interaction state (selection, mode, accordion, …).
//   render    — derived render outputs + a per-door prefix cache.
//   catalog   — re-fetchable catalogue data (not persisted).
// Actions are plain reducer-style immutable updates so wrapping the store in
// `immer` + `zundo` later is a drop-in with no structural change.
// ---------------------------------------------------------------------------

export interface DoorSelection {
  designId: string | null;
  colorId: string | null;
  windowOptionId: string | null;
  // Glass tint for the chosen window insert. Sessions persisted before this field rehydrate without it
  // (no persist-version bump) — read it defensively as null everywhere (`selection.glassTypeId ?? null`).
  glassTypeId: string | null;
  hardwareId: string | null;
  // Chosen insulation model (a construction id), when the dealer enabled the insulation picker. Non-visual
  // (doesn't affect the render); adds the model's upcharge to the price. Read defensively as null — sessions
  // persisted before this field rehydrate without it.
  constructionId: string | null;
}

export interface DoorObject {
  id: string;
  index: number; // detection order → "Door 1 / 2 / …"
  detected: DetectedObject; // immutable detection result (carries the server's single/double verdict)
  quad: NormalizedQuad; // current quad (user-edited or detected)
  // Current top-edge profile (user-edited or detected); null = flat. Sessions persisted before this
  // field rehydrate without it (no persist-version bump) — read it defensively (`topProfile ?? null`).
  topProfile?: TopProfile | null;
  selection: DoorSelection;
  // Manual single/double correction; when set it wins over the detected size (null = auto/detected).
  sizeOverride?: DoorSize | null;
}

// A postcard QR session: the landing code (quote submits go to the postcard-scoped endpoint,
// which writes the KNOWN dealer lead) + the door presets for the "Recommended for your home" rail.
export interface PostcardSession {
  code: string;
  salutation: string;
  presets: PostcardPreset[];
}

export interface VisualizerDocument {
  version: number;
  sessionId: string | null;
  photoUrl: string | null;
  collectionId: string | null;
  doors: Record<string, DoorObject>;
  doorOrder: string[];
  // Present only when the session was opened from a postcard landing link. Pre-feature persisted
  // sessions rehydrate without it — read defensively (`doc.postcard ?? null`).
  postcard?: PostcardSession | null;
  // The geocoded address this session's photo came from ("Use your address" flow) — prefills the
  // quote form so the customer never re-types what they already told us. Null for upload/phone/
  // sample photos; pre-feature persisted sessions rehydrate without it — read defensively.
  sourceAddress?: string | null;
}

export type SectionKey = "manufacturer" | "collection" | "design" | "color" | "layout" | "window" | "glass" | "hardware" | "insulation" | "";
export type EditorMode = "edit" | "compare";

// Sentinel `selectedDoorId` meaning "style every door at once". Houses with multiple garage doors
// usually get the SAME style on all of them (direct customer feedback), so this is the DEFAULT for
// multi-door sessions — clicking an individual door in the legend switches to per-door styling.
// Real door ids are "door-<n>" / "door-<uuid>", so the sentinel can never collide. Only STYLE picks
// gang across doors (see updateAllDoorSelections); geometry (quad/top shape/size) stays per-door.
export const ALL_DOORS = "all";

// The selection a fresh (or rehydrated) session starts on: ALL-doors for 2+ doors, the door itself
// for one. Shared by initFromExtraction/initFromPersonalization, removeDoor and the persist merge.
const defaultSelectedDoorId = (doorOrder: string[]): string | null =>
  doorOrder.length > 1 ? ALL_DOORS : doorOrder[0] ?? null;

export interface UiState {
  selectedDoorId: string | null;
  mode: EditorMode;
  openSection: SectionKey;
  fullscreen: boolean;
  comparePosition: number; // 0..100, handle starts mid-frame so the before/after reveal is visible on open
}

export type RenderStatus = "idle" | "rendering" | "blending" | "error";

export interface RenderCacheEntry {
  inputHash: string;
  baseUrl: string;
  resultUrl: string;
  // Display-preview variant of resultUrl (≤1280px WebP), when the server emitted one. Cached so a
  // chain served entirely from cache can still surface the final step's preview.
  previewUrl?: string | null;
}

// One door's last AI-blend outcome. Freshness is judged at READ time by comparing inputHash against
// the door's current blend inputs (same fields as the render cache key) — there is no imperative
// invalidation; editing a door simply makes its entry stale. `usedFallback` records that the server
// degraded that door to the geometric composite, so it must never read as "photoreal ready".
// baseUrl is the chain image the blend ran on — recorded for parity with RenderCacheEntry but
// deliberately NOT part of freshness: renderComposite clears the whole map whenever the composite
// under the blends changes, so a stale base can't survive to be compared.
export interface BlendEntry {
  inputHash: string;
  baseUrl: string;
  usedFallback: boolean;
}

export interface RenderState {
  status: RenderStatus;
  compositeUrl: string | null; // chained render of all configured doors (full-res, canonical)
  integratedUrl: string | null; // AI-blended composite (full-res, canonical)
  // Display-preview variants of the two urls above (≤1280px WebP, null when the server sent none).
  // The preview img shows these; compositeUrl/integratedUrl stay the chain/blend/fullscreen truth.
  compositePreviewUrl: string | null;
  integratedPreviewUrl: string | null;
  error: string | null;
  cache: Record<string, RenderCacheEntry>;
  // Per-door AI-blend state (ephemeral, like `cache`). Keyed by door id; see BlendEntry.
  blends: Record<string, BlendEntry>;
  epoch: number; // cancels stale async chains
}

export interface CatalogState {
  collection: CatalogCollection | null;
  collections: CatalogCollection[];
}

export const DOC_VERSION = 1;

// ---------------------------------------------------------------------------
// Durable session storage. The document survives tab closes — visualizer links arrive by QR code
// and text message, get opened, closed, and reopened, and losing the photo + fitted doors on a tab
// close is a conversion killer. localStorage instead of sessionStorage, with each write stamped
// with the dealer scope + timestamp so a read for a DIFFERENT dealer (one phone, many dealer
// links) or an EXPIRED session returns null and the flow starts fresh — a foreign session must
// never leak across dealers. Legacy sessionStorage sessions migrate on first read.
// ---------------------------------------------------------------------------
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** The dealer scope this page is running under, derivable OUTSIDE React (storage runs at boot):
 *  subdomain when present, else the leading path segment(s) — mirrors useCatalogScope's token. */
export function storageScopeToken(): string {
  const sub = getSubdomainTenant();
  if (sub) return sub;
  const parts = window.location.pathname.split("/").filter(Boolean);
  const head = (parts[0] ?? "").toLowerCase();
  return head === "manufacturer" ? `manufacturer/${(parts[1] ?? "").toLowerCase()}` : head;
}

interface StampedEntry {
  scope?: string;
  savedAt?: number;
  value?: string;
}

export const durableSessionStorage: StateStorage = {
  getItem: (name) => {
    try {
      const raw = localStorage.getItem(name);
      if (raw) {
        const wrapped = JSON.parse(raw) as StampedEntry;
        if (typeof wrapped?.value !== "string") return null;
        const expired = !wrapped.savedAt || Date.now() - wrapped.savedAt > SESSION_TTL_MS;
        if (expired || wrapped.scope !== storageScopeToken()) return null;
        return wrapped.value;
      }
      // Pre-durability sessions lived in sessionStorage — resume them once; the next write
      // lands in localStorage with a stamp.
      return sessionStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      const wrapped: StampedEntry = { scope: storageScopeToken(), savedAt: Date.now(), value };
      localStorage.setItem(name, JSON.stringify(wrapped));
    } catch {
      // Storage full / privacy mode — the session just won't survive the tab, as before.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
      sessionStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

const emptySelection = (): DoorSelection => ({ designId: null, colorId: null, windowOptionId: null, glassTypeId: null, hardwareId: null, constructionId: null });

// THE preset→selection mapping (used by both the landing seed and the rail apply, so a session
// opened from the QR link always carries exactly what tapping the same preset would).
// Presets are glass-agnostic by design — renders resolve without a tint.
const presetSelection = (p: PostcardPreset): DoorSelection => ({
  designId: p.selection.designId,
  colorId: p.selection.colorId,
  windowOptionId: p.selection.windowOptionId,
  glassTypeId: null,
  hardwareId: p.selection.hardwareId,
  // Postcard presets never carry an insulation choice.
  constructionId: null,
});

// A double-car-ish rectangle centred on the photo, used for a manually-added door. ~40% wide, 17.5% tall
// (≈16:7), so the homeowner drags the corners onto their real opening from a sensible starting point.
const centeredQuad = (): NormalizedQuad => {
  const cx = 0.5, cy = 0.55, hw = 0.2, hh = 0.0875;
  return {
    topLeft: { x: cx - hw, y: cy - hh },
    topRight: { x: cx + hw, y: cy - hh },
    bottomRight: { x: cx + hw, y: cy + hh },
    bottomLeft: { x: cx - hw, y: cy + hh },
  };
};

// A synthetic detection for a manually-added door, mirroring the shape the server returns so the rest of
// the pipeline (legend, render, pricing) treats it identically. Defaults to a double-car (16×7) opening.
const syntheticDetection = (quad: NormalizedQuad): DetectedObject => ({
  label: "garage_door",
  confidence: 1,
  boundingBox: { xMin: quad.topLeft.x, yMin: quad.topLeft.y, xMax: quad.bottomRight.x, yMax: quad.bottomRight.y },
  quadrilateral: quad,
  doorType: "double",
  maskSvgPath: null,
  topProfile: null,
  fitConfidence: 0, // 0 = unknown — a hand-placed door has no server fit to grade
  fitTier: null,
  occlusionSvgPath: null,
});

const initialDocument = (): VisualizerDocument => ({
  version: DOC_VERSION,
  sessionId: null,
  photoUrl: null,
  collectionId: null,
  doors: {},
  doorOrder: [],
  postcard: null,
  sourceAddress: null,
});

const initialUi = (): UiState => ({
  selectedDoorId: null,
  mode: "edit",
  openSection: "design",
  fullscreen: false,
  // Open the before/after mid-frame (not at the far-left edge, which showed 100% new door and hid the
  // homeowner's real house) so the transformation is the immediate hero moment.
  comparePosition: 50,
});

const initialRender = (): RenderState => ({
  status: "idle",
  compositeUrl: null,
  integratedUrl: null,
  compositePreviewUrl: null,
  integratedPreviewUrl: null,
  error: null,
  cache: {},
  blends: {},
  epoch: 0,
});

const initialCatalog = (): CatalogState => ({ collection: null, collections: [] });

interface VisualizerState {
  document: VisualizerDocument;
  ui: UiState;
  render: RenderState;
  catalog: CatalogState;

  // ---- document ----
  initFromExtraction: (photoUrl: string, sessionId: string | null, objects: DetectedObject[]) => void;
  // Seed a session from a postcard QR landing payload: frozen photo + persisted detections +
  // the hero preset applied to every door (no re-extraction).
  initFromPersonalization: (code: string, payload: PostcardLandingPayload) => void;
  // Apply a rail preset: switch the collection AND re-apply its selection to EVERY door (the
  // document-level collectionId wipe makes a per-door apply meaningless — spec §6).
  applyPreset: (preset: PostcardPreset) => void;
  setCollection: (collectionId: string | null) => void;
  updateDoorSelection: (doorId: string, patch: Partial<DoorSelection>) => void;
  // ALL-doors mode style pick: apply the patch to EVERY door. The rail-preset applyPreset already
  // re-applies a full selection to every door — this is its patch-level sibling for the pickers.
  updateAllDoorSelections: (patch: Partial<DoorSelection>) => void;
  setDoorQuad: (doorId: string, quad: NormalizedQuad) => void;
  // Set/clear the door's top-edge profile (null = flat). Render invalidation happens via the
  // per-door inputHash (the profile participates in the cache key), same as a quad edit.
  setDoorTopProfile: (doorId: string, profile: TopProfile | null) => void;
  setDoorSizeOverride: (doorId: string, size: DoorSize | null) => void;
  // Record the address the session's photo was resolved from (quote-form prefill). Called by the
  // Select page AFTER initFromExtraction (which resets the document, address included).
  setSourceAddress: (address: string | null) => void;
  // Manually add a door at the centre of the photo (behaves exactly like a detected one). Returns its id.
  addDoor: () => string;
  // Remove a door, renumber the rest, repoint the selection, and reset the render so it re-composites.
  removeDoor: (doorId: string) => void;
  resetAll: () => void;

  // ---- ui ----
  selectDoor: (doorId: string) => void;
  setMode: (mode: EditorMode) => void;
  setOpenSection: (section: SectionKey) => void;
  setFullscreen: (v: boolean) => void;
  setComparePosition: (pos: number) => void;

  // ---- catalog ----
  setCatalog: (patch: Partial<CatalogState>) => void;

  // ---- render ----
  setRender: (patch: Partial<RenderState>) => void;
  bumpEpoch: () => number;
  setCacheEntry: (doorId: string, entry: RenderCacheEntry) => void;
  setBlendEntry: (doorId: string, entry: BlendEntry) => void;
}

export const useVisualizer = create<VisualizerState>()(
  persist(
    (set, get) => ({
      document: initialDocument(),
      ui: initialUi(),
      render: initialRender(),
      catalog: initialCatalog(),

      initFromExtraction: (photoUrl, sessionId, objects) => {
        const doors: Record<string, DoorObject> = {};
        const doorOrder: string[] = [];
        objects.forEach((o, i) => {
          const id = `door-${i}`;
          doors[id] = { id, index: i, detected: o, quad: o.quadrilateral, topProfile: o.topProfile ?? null, selection: emptySelection() };
          doorOrder.push(id);
        });
        set({
          document: { version: DOC_VERSION, sessionId, photoUrl, collectionId: null, doors, doorOrder, postcard: null, sourceAddress: null },
          ui: { ...initialUi(), selectedDoorId: defaultSelectedDoorId(doorOrder) },
          render: initialRender(),
        });
      },

      initFromPersonalization: (code, payload) => {
        // The hero preset (the door printed large on the card) starts selected on every door.
        const hero = payload.presets[0] ?? null;
        const selection = (): DoorSelection => (hero ? presetSelection(hero) : emptySelection());

        const doors: Record<string, DoorObject> = {};
        const doorOrder: string[] = [];
        payload.objects.forEach((o, i) => {
          const id = `door-${i}`;
          const quad = o.quad;
          const xs = [quad.topLeft.x, quad.topRight.x, quad.bottomRight.x, quad.bottomLeft.x];
          const ys = [quad.topLeft.y, quad.topRight.y, quad.bottomRight.y, quad.bottomLeft.y];
          // Rehydrate the persisted postcard detection into the extraction shape the rest of the
          // pipeline expects; doorType carries the size verdict into resolve/price params.
          const detected: DetectedObject = {
            label: o.label,
            confidence: o.confidence,
            boundingBox: { xMin: Math.min(...xs), yMin: Math.min(...ys), xMax: Math.max(...xs), yMax: Math.max(...ys) },
            quadrilateral: quad,
            doorType: o.doorType === "single" ? "single" : "double",
            maskSvgPath: null,
            topProfile: o.topProfile ?? null,
          };
          doors[id] = { id, index: i, detected, quad, topProfile: o.topProfile ?? null, selection: selection() };
          doorOrder.push(id);
        });
        set({
          document: {
            version: DOC_VERSION,
            sessionId: null,
            photoUrl: payload.photoUrl,
            collectionId: hero?.selection.collectionId ?? null,
            doors,
            doorOrder,
            postcard: { code, salutation: payload.salutation, presets: payload.presets },
          },
          // Postcard sessions seed the SAME preset on every door, so the ALL-doors default (2+
          // doors) matches what the customer already sees — same rule as initFromExtraction.
          ui: { ...initialUi(), selectedDoorId: defaultSelectedDoorId(doorOrder) },
          render: initialRender(),
        });
      },

      applyPreset: (preset) =>
        set((s) => {
          const doors: Record<string, DoorObject> = {};
          for (const id of s.document.doorOrder) {
            doors[id] = { ...s.document.doors[id], selection: presetSelection(preset) };
          }
          return {
            document: { ...s.document, collectionId: preset.selection.collectionId, doors },
            // Same invalidation as setCollection: every render is stale; bump the epoch to cancel
            // any in-flight chain.
            render: { ...initialRender(), epoch: s.render.epoch + 1 },
          };
        }),

      setCollection: (collectionId) =>
        set((s) => {
          // Changing collection invalidates every door's selection + all renders.
          const doors: Record<string, DoorObject> = {};
          for (const id of s.document.doorOrder) doors[id] = { ...s.document.doors[id], selection: emptySelection() };
          return {
            document: { ...s.document, collectionId, doors },
            render: { ...initialRender(), epoch: s.render.epoch },
          };
        }),

      updateDoorSelection: (doorId, patch) =>
        set((s) => {
          const door = s.document.doors[doorId];
          if (!door) return s;
          return {
            document: {
              ...s.document,
              doors: { ...s.document.doors, [doorId]: { ...door, selection: { ...door.selection, ...patch } } },
            },
          };
        }),

      updateAllDoorSelections: (patch) =>
        set((s) => {
          // Same per-door merge as updateDoorSelection, fanned out over every door. Doors with
          // DIFFERENT current selections keep their unpatched fields (mixed until a later pick
          // unifies them) — only the picked field(s) become uniform.
          const doors: Record<string, DoorObject> = {};
          for (const id of s.document.doorOrder) {
            const door = s.document.doors[id];
            doors[id] = { ...door, selection: { ...door.selection, ...patch } };
          }
          return { document: { ...s.document, doors } };
        }),

      setDoorQuad: (doorId, quad) =>
        set((s) => {
          const door = s.document.doors[doorId];
          if (!door) return s;
          return {
            document: { ...s.document, doors: { ...s.document.doors, [doorId]: { ...door, quad } } },
          };
        }),

      setDoorTopProfile: (doorId, profile) =>
        set((s) => {
          const door = s.document.doors[doorId];
          if (!door) return s;
          return {
            document: { ...s.document, doors: { ...s.document.doors, [doorId]: { ...door, topProfile: profile } } },
          };
        }),

      setDoorSizeOverride: (doorId, size) =>
        set((s) => {
          const door = s.document.doors[doorId];
          if (!door) return s;
          return {
            document: { ...s.document, doors: { ...s.document.doors, [doorId]: { ...door, sizeOverride: size } } },
          };
        }),

      setSourceAddress: (sourceAddress) => set((s) => ({ document: { ...s.document, sourceAddress } })),

      addDoor: () => {
        const s = get();
        if (!s.document.photoUrl) return "";
        const id = `door-${crypto.randomUUID()}`;
        const quad = centeredQuad();
        const maxIndex = s.document.doorOrder.reduce((m, did) => Math.max(m, s.document.doors[did]?.index ?? -1), -1);
        const door: DoorObject = { id, index: maxIndex + 1, detected: syntheticDetection(quad), quad, topProfile: null, selection: emptySelection() };
        set((st) => ({
          document: {
            ...st.document,
            doors: { ...st.document.doors, [id]: door },
            doorOrder: [...st.document.doorOrder, id],
          },
        }));
        return id;
      },

      removeDoor: (doorId) =>
        set((s) => {
          if (!s.document.doors[doorId]) return s;
          const doorOrder = s.document.doorOrder.filter((id) => id !== doorId);
          // Renumber densely by position so "Door N" labels stay contiguous (render order follows doorOrder,
          // and the render cache key excludes index, so renumbering is safe for the composite chain).
          const doors: Record<string, DoorObject> = {};
          doorOrder.forEach((id, i) => { doors[id] = { ...s.document.doors[id], index: i }; });
          // ALL-doors mode survives a removal while 2+ doors remain; with one door left there is
          // nothing to gang, so drop to that door (mirrors the single-door init default).
          // Individual mode is unchanged: removing the selected door falls back to the first.
          const selectedDoorId =
            s.ui.selectedDoorId === ALL_DOORS
              ? defaultSelectedDoorId(doorOrder)
              : s.ui.selectedDoorId === doorId ? (doorOrder[0] ?? null) : s.ui.selectedDoorId;
          return {
            document: { ...s.document, doors, doorOrder },
            ui: { ...s.ui, selectedDoorId },
            // Removing a door changes every downstream base image — drop the whole prefix cache, cancel any
            // in-flight chain (epoch bump), and clear the stale composite so the photo shows until re-render.
            // Blends ride on the composite, so they all go stale with it too.
            render: { ...s.render, cache: {}, blends: {}, epoch: s.render.epoch + 1, compositeUrl: null, integratedUrl: null, compositePreviewUrl: null, integratedPreviewUrl: null, status: "idle", error: null },
          };
        }),

      resetAll: () => set({ document: initialDocument(), ui: initialUi(), render: initialRender(), catalog: initialCatalog() }),

      selectDoor: (doorId) => set((s) => ({ ui: { ...s.ui, selectedDoorId: doorId } })),
      setMode: (mode) => set((s) => ({ ui: { ...s.ui, mode } })),
      setOpenSection: (openSection) => set((s) => ({ ui: { ...s.ui, openSection } })),
      setFullscreen: (fullscreen) => set((s) => ({ ui: { ...s.ui, fullscreen } })),
      setComparePosition: (comparePosition) => set((s) => ({ ui: { ...s.ui, comparePosition } })),

      setCatalog: (patch) => set((s) => ({ catalog: { ...s.catalog, ...patch } })),

      setRender: (patch) => set((s) => ({ render: { ...s.render, ...patch } })),
      bumpEpoch: () => {
        const epoch = get().render.epoch + 1;
        set((s) => ({ render: { ...s.render, epoch } }));
        return epoch;
      },
      setCacheEntry: (doorId, entry) =>
        set((s) => ({ render: { ...s.render, cache: { ...s.render.cache, [doorId]: entry } } })),
      setBlendEntry: (doorId, entry) =>
        set((s) => ({ render: { ...s.render, blends: { ...s.render.blends, [doorId]: entry } } })),
    }),
    {
      name: "rg.visualizer.v1",
      storage: createJSONStorage(() => durableSessionStorage),
      version: DOC_VERSION,
      // Persist only the reconstructable document; UI/render/catalog are ephemeral.
      partialize: (s) => ({ document: s.document }),
      // Rehydrate: ui is never persisted, so re-derive the door-selection default from the restored
      // document (ALL-doors for 2+ doors, the door itself for one) — the same rule the init actions
      // apply. Without this a mid-session refresh left nothing selected and style picks no-oped.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<VisualizerState>;
        const doorOrder = p.document?.doorOrder ?? [];
        return { ...current, ...p, ui: { ...current.ui, selectedDoorId: defaultSelectedDoorId(doorOrder) } };
      },
    },
  ),
);
